import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  from,
  ApolloLink,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { offsetLimitPagination, captureGQLException, AUTHENTICATION_SCOPES } from '@playbookux/playbook-web-core';

import { config } from '../../config';

class GraphQL {
  constructor() {
    let uri = config.REACT_APP_PLAYBOOKUX_GRAPHQL_URI;
    const authHandler = new ApolloLink((operation, forward) => {
      if (this.scope === AUTHENTICATION_SCOPES.USER) {
        uri = config.REACT_APP_PLAYBOOKUX_GRAPHQL_URI;
      } else if (this.scope === AUTHENTICATION_SCOPES.PUBLIC) {
        uri = config.REACT_APP_PLAYBOOKUX_GRAPHQL_PUBLIC_URI;
      } else if (this.scope === AUTHENTICATION_SCOPES.TESTER) {
        uri = config.REACT_APP_PLAYBOOKUX_GRAPHQL_TESTER_URI;
      } else if (
        this.scope === AUTHENTICATION_SCOPES.SHARE_CLIP
        || this.scope === AUTHENTICATION_SCOPES.SHARE_HIGHLIGHT_REEL
        || this.scope === AUTHENTICATION_SCOPES.SHARE_PROJECT
        || this.scope === AUTHENTICATION_SCOPES.REPORT
      ) {
        const shareUri = config.REACT_APP_PLAYBOOKUX_SHARE_GRAPHQL_URI;
        uri = shareUri.replace(':share_type', this.scope);
      } else if (this.scope === AUTHENTICATION_SCOPES.LIVESTREAM) {
        uri = config.REACT_APP_PLAYBOOKUX_LIVESTREAM_GRAPHQL_URI;
      }

      const headers = {};

      if (this.token) {
        headers.authorization = `Bearer ${this.token}`;
      }

      // add the authorization to the headers
      operation.setContext({
        uri,
        headers,
      });

      return forward(operation);
    });

    const httpLink = new HttpLink({ uri });

    const errorHandler = onError(({
      networkError, graphQLErrors, operation,
    }) => {
      if (graphQLErrors && graphQLErrors.length > 0) {
        // get the query/mutation name - message - path
        captureGQLException(operation.operationName, `GraphQL Error: ${graphQLErrors[0].message}`);
      }

      // add the errors handling
      if (networkError && networkError.statusCode === 401) {
        if (this.unauthorizedListener) {
          this.unauthorizedListener(networkError);
        }
      }
    });

    const client = new ApolloClient({
      cache: new InMemoryCache({
        typePolicies: {
          Query: {
            fields: {
              search: offsetLimitPagination([
                'companyId',
                'order',
                'searchableType',
                'search',
                'projectIds',
              ]),
              testSessionNotes: offsetLimitPagination([
                'testSessionId',
                'order',
                'companyId',
                'noteType',
                'search',
              ]),
              highlightReels: offsetLimitPagination([
                'companyId',
                'order',
                'search',
              ]),
              testers: offsetLimitPagination([
                'projectId',
                'approved',
                'isCompleted',
                'tsIsApproved',
                'removed',
                'isPending',
                'isExpired',
              ]),
              pastTesters: offsetLimitPagination(['companyId']),
              companies: offsetLimitPagination(['search']),
              findMatchedProjects: offsetLimitPagination(['userId', 'filters']),
              userEligibilities: offsetLimitPagination(['companyId']),
              testerPayments: offsetLimitPagination([]),
              templates: offsetLimitPagination([
                'companyId',
                'templateType',
              ]),
              users: offsetLimitPagination([
                'search',
              ]),
              projects: offsetLimitPagination([
                'order',
                'includeDeletedProjects',
                'companyId',
                'name',
                'description',
                'status',
                'folderIds',
                'projectCreatorsIds',
              ]),
              approvals: offsetLimitPagination(['participantSource']),
              practiceTests: offsetLimitPagination(['search']),
              projectParticipants: offsetLimitPagination([
                'projectId',
                'approved',
                'isCompleted',
                'tsIsApproved',
                'removed',
              ]),
              tagAnalytics: offsetLimitPagination([
                'id',
                'tag',
                'totalPeriod',
                '__typename',
              ]),
              tagHighlights: offsetLimitPagination([
                'charEnd',
                'charStart',
                'createdAt',
                'createdBy',
                'entity',
                'entityType',
                'id',
                'project',
                'tag',
                '__typename',
              ]),
              usageLogsByCompany: offsetLimitPagination([
                'companyId',
              ]),
              surveysAnalytics: {
                keyArgs: ['projectId', 'segmentId', 'profileIds'],
                merge(existing = [], incoming) {
                  return [...existing, ...incoming];
                },
              },
              manageAudience: offsetLimitPagination([
                'companyId',
                'projectId',
                'screenerVersion',
                'questionOrder',
              ]),
            },
          },
        },
      }),
      link: from([authHandler, errorHandler, httpLink]),
    });

    this.client = client;

    this.client.defaultOptions = {
      watchQuery: {
        fetchPolicy: 'network-only',
      },
      query: {
        fetchPolicy: 'network-only',
      },
    };

    this.token = null;
    this.scope = null;
    this.unauthorizedListener = null;
  }

  setToken(token) {
    this.token = token;
  }

  setScope(scope) {
    this.scope = scope;
  }

  getScope() {
    return this.scope;
  }

  setUnauthorizedListener(listener) {
    this.unauthorizedListener = listener;
  }

  getClient() {
    return this.client;
  }

  query = (options) => this.client.query(options);

  mutate = (options) => this.client.mutate(options);
}

export default new GraphQL();
